import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Zap, Mail } from 'lucide-react';

import AutomationHub from '../components/marketing/automation/AutomationHub';
import NewsletterTab from '../components/marketing/NewsletterTab';

const MarketingPage = () => {
  const { API, getAuthHeaders } = useAuth();
  const [automations, setAutomations] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [waConnected, setWaConnected] = useState(false);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [autoRes, nlRes, custRes, connRes, brandRes, companyRes] = await Promise.all([
        axios.get(`${API}/marketing/automations`, getAuthHeaders()),
        axios.get(`${API}/marketing/newsletters`, getAuthHeaders()),
        axios.get(`${API}/customers`, getAuthHeaders()),
        axios.get(`${API}/social/connections`, getAuthHeaders()),
        axios.get(`${API}/platform-branding`).catch(() => ({ data: {} })),
        axios.get(`${API}/company-profile`, getAuthHeaders()).catch(() => ({ data: {} })),
      ]);
      setAutomations(autoRes.data);
      setNewsletters(nlRes.data);
      setCustomers(custRes.data);
      const hasWa = (connRes.data || []).some(c => c.platform === 'whatsapp' && c.is_active);
      setWaConnected(hasWa);
      // Merge branding: company profile takes priority, fallback to platform
      const bp = brandRes.data || {};
      const cp = companyRes.data || {};
      setBranding({
        primary_color: bp.primary_color || null,
        logo_url: cp.logo_url ? `${API}${cp.logo_url}` : (bp.logo_url ? `${API}${bp.logo_url}` : null),
        brand_name: cp.nome_brand || bp.brand_name || null,
      });
    } catch (error) {
      console.error('Error fetching marketing data:', error);
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="marketing-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-outfit">Marketing</h1>
        <p className="text-muted-foreground mt-1">Messaggi automatici e newsletter per i tuoi clienti</p>
      </div>

      <Tabs defaultValue="automation" className="space-y-6">
        <TabsList className="w-full sm:w-auto flex justify-start gap-1 h-auto p-1 sm:inline-flex" data-testid="marketing-tabs">
          <TabsTrigger value="automation" data-testid="tab-automation" className="flex-1 sm:flex-none text-sm py-2">
            <Zap className="w-4 h-4 mr-2" />Automazioni
          </TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="tab-newsletter" className="flex-1 sm:flex-none text-sm py-2">
            <Mail className="w-4 h-4 mr-2" />Newsletter
          </TabsTrigger>
        </TabsList>

        <TabsContent value="automation">
          <AutomationHub automations={automations} onRefresh={fetchData} waConnected={waConnected} />
        </TabsContent>

        <TabsContent value="newsletter">
          <NewsletterTab newsletters={newsletters} customers={customers} onRefresh={fetchData} branding={branding} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MarketingPage;
